import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCheck,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/site";
import { getAttribution } from "@/lib/utm";

// One question at a time — conversational flow
const QUESTIONS = [
  {
    id: "name",
    field: "name",
    bot: "Hi 👋 I'm Swift Assist. Before we begin — what should I call you?",
    kind: "text",
    placeholder: "Your full name",
    validate: (v) => (v && v.trim().length >= 2 ? null : "Please share your name."),
  },
  {
    id: "phone",
    field: "phone",
    bot: (a) => `Great to meet you, ${a.name.split(" ")[0]}! What's your mobile number? Raj will call you back on this.`,
    kind: "text",
    inputType: "tel",
    placeholder: "10-digit mobile",
    validate: (v) => (v && v.replace(/\D/g, "").length >= 10 ? null : "Enter a valid 10-digit number."),
  },
  {
    id: "email",
    field: "email",
    bot: "And your email — so we can send you a summary?",
    kind: "text",
    inputType: "email",
    placeholder: "you@email.com",
    validate: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "") ? null : "That email doesn't look right."),
  },
  {
    id: "loan_type",
    field: "loan_type",
    bot: "What kind of loan can we help you with?",
    kind: "chips",
    options: [
      { v: "home_loan", l: "🏠 Home Loan" },
      { v: "business_loan", l: "💼 Business Loan" },
      { v: "lap_lrd", l: "🏢 LAP / LRD" },
      { v: "personal_loan", l: "💳 Personal Loan" },
      { v: "balance_transfer", l: "🔁 Balance Transfer" },
      { v: "project_finance", l: "🏗️ Project Finance" },
      { v: "other", l: "🤔 Not sure yet" },
    ],
    labelize: (v, q) => q.options.find((o) => o.v === v)?.l || v,
  },
  {
    id: "loan_amount",
    field: "loan_amount",
    bot: "Roughly how much are you looking for?",
    kind: "chips_or_text",
    options: [
      { v: "Under ₹10 L", l: "Under ₹10 L" },
      { v: "₹10 - 25 L", l: "₹10 - 25 L" },
      { v: "₹25 L - 1 Cr", l: "₹25 L - 1 Cr" },
      { v: "₹1 - 5 Cr", l: "₹1 - 5 Cr" },
      { v: "₹5 Cr+", l: "₹5 Cr+" },
    ],
    placeholder: "Or type your own (e.g. ₹42 L)",
  },
  {
    id: "employment_type",
    field: "employment_type",
    bot: "What best describes you?",
    kind: "chips",
    options: [
      { v: "salaried", l: "👔 Salaried" },
      { v: "self_employed", l: "🩺 Self-Employed / Professional" },
      { v: "business_owner", l: "🏭 Business Owner / MSME" },
      { v: "property_investor", l: "🏢 Property Investor" },
      { v: "other", l: "🤔 Other" },
    ],
    labelize: (v, q) => q.options.find((o) => o.v === v)?.l || v,
  },
  {
    id: "income",
    field: "net_monthly_takehome",
    bot: (a) =>
      a.employment_type === "salaried"
        ? "What's your net monthly take-home (₹)? Approximate is fine."
        : "What's your approximate monthly income (₹)?",
    kind: "chips_or_text",
    // Skip for business, we'll ask turnover instead
    skipIf: (a) => a.employment_type === "self_employed" || a.employment_type === "business_owner",
    options: [
      { v: "50000", l: "₹50 K" },
      { v: "100000", l: "₹1 L" },
      { v: "200000", l: "₹2 L" },
      { v: "500000", l: "₹5 L+" },
    ],
    placeholder: "e.g. 150000",
    inputType: "number",
  },
  {
    id: "turnover",
    field: "annual_turnover",
    bot: "What's your annual turnover / receipts (₹)? Approximate is fine.",
    kind: "chips_or_text",
    skipIf: (a) => a.employment_type !== "self_employed" && a.employment_type !== "business_owner",
    options: [
      { v: "5000000", l: "₹50 L" },
      { v: "10000000", l: "₹1 Cr" },
      { v: "50000000", l: "₹5 Cr" },
      { v: "100000000", l: "₹10 Cr+" },
    ],
    placeholder: "e.g. 25000000",
    inputType: "number",
  },
  {
    id: "collateral",
    field: "has_property_collateral",
    bot: "Do you have a property to offer as collateral? (helps unlock LAP / secured options at lower rates)",
    kind: "chips",
    options: [
      { v: "yes", l: "✅ Yes" },
      { v: "no", l: "❌ No" },
      { v: "not_sure", l: "🤷 Not sure" },
    ],
    transform: (v) => v === "yes",
    labelize: (v, q) => q.options.find((o) => o.v === v)?.l || v,
  },
  {
    id: "message",
    field: "message",
    bot: "Anything else Raj should know? (optional)",
    kind: "text",
    placeholder: "e.g. timeline, urgency, prior bank rejections",
    optional: true,
    inputType: "textarea",
  },
];

const TYPING_MS = 700;

export default function ApplyChat() {
  const [messages, setMessages] = useState([]); // {from:'bot'|'user', text, ts}
  const [answers, setAnswers] = useState({});
  const [qIndex, setQIndex] = useState(0);
  const [botTyping, setBotTyping] = useState(false);
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);
  const bootedRef = useRef(false);
  const busyRef = useRef(false); // prevents double-submit while bot is typing / advancing

  // Compute the visible/answerable question index (skipping conditionally)
  const currentQ = (() => {
    let idx = qIndex;
    while (idx < QUESTIONS.length && QUESTIONS[idx].skipIf && QUESTIONS[idx].skipIf(answers)) {
      idx += 1;
    }
    return { q: QUESTIONS[idx], idx };
  })();

  const totalAnswerable = QUESTIONS.filter((q) => !q.skipIf || !q.skipIf(answers)).length;
  const answeredCount = Object.keys(answers).length;
  const progress = Math.min(100, Math.round((answeredCount / totalAnswerable) * 100));

  // Initial greeting + first question — guarded so StrictMode double-invoke doesn't duplicate the boot
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    (async () => {
      await pushBot("Hey! Ready to explore your loan options? It'll take ~90 seconds.");
      await askNext(0, {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, botTyping]);

  const pushBot = (text) =>
    new Promise((resolve) => {
      setBotTyping(true);
      setTimeout(() => {
        setBotTyping(false);
        setMessages((m) => [...m, { from: "bot", text, ts: Date.now() }]);
        setTimeout(resolve, 100);
      }, TYPING_MS);
    });

  const pushUser = (text) => {
    setMessages((m) => [...m, { from: "user", text, ts: Date.now() }]);
  };

  // askNext advances from startIdx, using the freshly-computed `answersSnapshot`
  // for both skipIf evaluation AND dynamic bot text (q.bot may read prior answers).
  const askNext = async (startIdx, answersSnapshot) => {
    let idx = startIdx;
    while (idx < QUESTIONS.length && QUESTIONS[idx].skipIf && QUESTIONS[idx].skipIf(answersSnapshot)) {
      idx += 1;
    }
    if (idx >= QUESTIONS.length) {
      await finishFlow(answersSnapshot);
      return;
    }
    const q = QUESTIONS[idx];
    const botText = typeof q.bot === "function" ? q.bot(answersSnapshot) : q.bot;
    setQIndex(idx);
    await pushBot(botText);
    setInput("");
  };

  const answer = async (rawVal, displayVal) => {
    if (busyRef.current || done) return;
    const q = currentQ.q;
    if (!q) return;
    // Validation
    if (q.validate && !q.optional) {
      const err = q.validate(rawVal);
      if (err) return toast.error(err);
    }
    // Transform for storage
    const stored = q.transform ? q.transform(rawVal) : rawVal;
    const label = displayVal || (q.labelize ? q.labelize(rawVal, q) : rawVal);
    pushUser(label);
    const nextAnswers = { ...answers, [q.field]: stored };
    setAnswers(nextAnswers);
    setInput("");
    busyRef.current = true;
    try {
      // Advance synchronously using the fresh snapshot — no stale-closure races
      await askNext(currentQ.idx + 1, nextAnswers);
    } finally {
      busyRef.current = false;
    }
  };

  const finishFlow = async (finalAnswers) => {
    setSubmitting(true);
    try {
      await pushBot("Perfect — let me send this over to Raj…");
      const attr = getAttribution();
      const a = finalAnswers || answers;
      const payload = {
        application_type: "full",
        apply_method: "chat",
        name: a.name || "",
        email: a.email || "",
        phone: a.phone || "",
        loan_type: a.loan_type || "other",
        loan_amount: a.loan_amount || null,
        employment_type: a.employment_type || "other",
        city: "Bangalore",
        message: a.message || "Submitted via chat flow",
        net_monthly_takehome:
          a.net_monthly_takehome && !isNaN(+a.net_monthly_takehome) ? +a.net_monthly_takehome : null,
        annual_turnover: a.annual_turnover && !isNaN(+a.annual_turnover) ? +a.annual_turnover : null,
        has_property_collateral:
          typeof a.has_property_collateral === "boolean" ? a.has_property_collateral : null,
        consent_credit_pull: true,
        consent_terms: true,
        persona: attr.persona,
        landing_page: attr.landing_page || window.location.pathname,
        utm_source: attr.utm_source,
        utm_medium: attr.utm_medium,
        utm_campaign: attr.utm_campaign,
        utm_content: attr.utm_content,
        utm_term: attr.utm_term,
        referrer: attr.referrer,
      };
      await api.post("/inquiries", payload);
      await pushBot(
        `Done ✅  You'll get a call from ${BRAND.phone} within one business day. Would you like to continue this chat on WhatsApp?`
      );
      setDone(true);
    } catch (e) {
      await pushBot("Hmm — something didn't go through. Please try again or ping us on WhatsApp.");
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onTextSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() && !currentQ.q?.optional) return;
    answer(input.trim() || "—");
  };

  const q = currentQ.q;
  const showChipsOnly = q?.kind === "chips";
  const showChipsAndText = q?.kind === "chips_or_text";
  const showTextOnly = q?.kind === "text";

  const waHref = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    `Hi Raj, just submitted my loan inquiry via the chat on your website. Name: ${answers.name || "—"}. Please continue here.`
  )}`;

  return (
    <div data-testid="apply-chat-page" className="container-x py-8 md:py-12">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/apply-form" className="btn-ghost text-xs">
            <ArrowLeft className="h-3.5 w-3.5" /> Prefer the classic form?
          </Link>
          <span className="text-xs text-brand-sage" data-testid="chat-progress">
            {progress}%
          </span>
        </div>

        {/* Chat window */}
        <div className="rounded-3xl overflow-hidden shadow-[0_30px_60px_-24px_rgba(17,42,34,0.35)] border border-brand-sand bg-[#ECE5DD]">
          {/* Header */}
          <div className="bg-brand-forest text-brand-cream px-5 py-3.5 flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-brand-gold text-brand-forest font-outfit font-extrabold">
              S
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-outfit font-semibold text-sm">Swift Assist · Loan Team</div>
              <div className="text-[11px] text-brand-cream/70 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> online · replies in seconds
              </div>
            </div>
            <div className="text-[10px] text-brand-cream/60 hidden sm:block">Secure · Free</div>
          </div>

          {/* Progress */}
          <div className="h-1 bg-brand-sand overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-terracotta to-brand-gold transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="h-[440px] sm:h-[520px] overflow-y-auto px-4 py-5 space-y-2.5"
            style={{
              backgroundImage:
                "linear-gradient(0deg, rgba(255,255,255,0.6), rgba(255,255,255,0.6)), radial-gradient(rgba(17,42,34,0.05) 1px, transparent 1px)",
              backgroundSize: "auto, 14px 14px",
            }}
          >
            {messages.map((m, i) => (
              <Bubble key={i} from={m.from} text={m.text} ts={m.ts} />
            ))}
            {botTyping && <TypingBubble />}
          </div>

          {/* Input area */}
          <div className="bg-brand-cream/95 border-t border-brand-sand px-4 py-3">
            {done ? (
              <div data-testid="chat-done" className="flex flex-col items-center py-2 gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-terracotta/10 text-brand-terracotta">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div className="text-center">
                  <div className="font-outfit font-bold text-brand-forest">Application received.</div>
                  <div className="text-xs text-brand-sage mt-1">Raj will personally reach out shortly.</div>
                </div>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="chat-wa-continue"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#1ea854] transition-colors"
                >
                  <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
                </a>
                <Link to="/" className="text-xs font-medium text-brand-sage hover:text-brand-forest">
                  Back to home
                </Link>
              </div>
            ) : botTyping || submitting ? (
              <div className="text-xs text-brand-sage py-2 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Swift Assist is typing…
              </div>
            ) : (
              <>
                {(showChipsOnly || showChipsAndText) && q?.options && (
                  <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-chips">
                    {q.options.map((o) => (
                      <button
                        key={o.v}
                        onClick={() => answer(o.v, o.l)}
                        data-testid={`chip-${q.id}-${o.v}`}
                        className="rounded-full border border-brand-forest/20 bg-white px-3.5 py-1.5 text-xs font-semibold text-brand-forest hover:bg-brand-forest hover:text-brand-cream transition-colors"
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}
                {(showTextOnly || showChipsAndText) && (
                  <form onSubmit={onTextSubmit} className="flex items-center gap-2">
                    {q?.inputType === "textarea" ? (
                      <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={q?.placeholder || "Type a reply..."}
                        rows={2}
                        data-testid="chat-input"
                        className="flex-1 resize-none rounded-2xl border border-brand-sand bg-white px-3.5 py-2 text-sm text-brand-forest placeholder:text-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-terracotta"
                      />
                    ) : (
                      <input
                        type={q?.inputType || "text"}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={q?.placeholder || "Type a reply..."}
                        autoFocus
                        data-testid="chat-input"
                        className="flex-1 rounded-full border border-brand-sand bg-white px-4 py-2 text-sm text-brand-forest placeholder:text-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-terracotta"
                      />
                    )}
                    <button
                      type="submit"
                      aria-label="Send"
                      data-testid="chat-send"
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-terracotta text-white hover:bg-[#b25535] transition-colors"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </form>
                )}
                {q?.optional && (
                  <button
                    onClick={() => answer("—", "Skip")}
                    className="mt-2 text-[11px] font-medium text-brand-sage hover:text-brand-forest"
                    data-testid="chat-skip"
                  >
                    Skip this
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-brand-sage max-w-lg mx-auto">
          Your details go directly to Raj — never sold or shared. Documents (PAN, Aadhaar, bank statements) are collected securely over WhatsApp / email after the first call.
        </p>
      </div>
    </div>
  );
}

function Bubble({ from, text, ts }) {
  const isBot = from === "bot";
  return (
    <div
      className={`flex ${isBot ? "justify-start" : "justify-end"} animate-fade-up`}
      data-testid={`chat-bubble-${from}`}
    >
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2 shadow-sm text-sm leading-snug ${
          isBot
            ? "bg-white text-brand-forest rounded-tl-sm"
            : "bg-[#D9FDD3] text-brand-forest rounded-tr-sm"
        }`}
      >
        <div className="whitespace-pre-line">{text}</div>
        <div className={`mt-1 flex items-center gap-1 text-[10px] ${isBot ? "text-brand-sage" : "text-emerald-800/70 justify-end"}`}>
          {new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          {!isBot && <CheckCheck className="h-3 w-3" />}
        </div>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start" data-testid="chat-typing">
      <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-brand-sage/70 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="h-2 w-2 rounded-full bg-brand-sage/70 animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="h-2 w-2 rounded-full bg-brand-sage/70 animate-bounce" style={{ animationDelay: "240ms" }} />
        </div>
      </div>
    </div>
  );
}
